import {useCallback, useEffect, useRef, useState} from 'react';

/** Long enough for a slow dialog, short enough that a stuck indicator does not linger. */
const PICKER_PENDING_TIMEOUT_MS = 8000;

/**
 * The native file dialog can take a moment to appear with nothing on screen saying so.
 * `pending` covers exactly that gap: the window losing focus means the dialog is up.
 *
 * The listeners are attached inside `startPending`, before the caller opens the picker —
 * from an effect they would go up after React commits, by which time the blur that ends
 * the wait may already have fired.
 */
export function useFilePickerPending() {
    const [pending, setPending] = useState(false);
    const stopRef = useRef<() => void>(() => {});

    const startPending = useCallback(() => {
        stopRef.current();
        setPending(true);

        const timeoutId = window.setTimeout(() => stopRef.current(), PICKER_PENDING_TIMEOUT_MS);
        const stop = () => {
            stopRef.current = () => {};
            window.clearTimeout(timeoutId);
            // The dialog belongs to the OS, so window focus changes are the only signal it gives.
            window.removeEventListener('blur', stop);
            window.removeEventListener('focus', stop);
            setPending(false);
        };

        stopRef.current = stop;
        window.addEventListener('blur', stop);
        window.addEventListener('focus', stop);
    }, []);

    useEffect(() => () => stopRef.current(), []);

    return {pending, startPending};
}
